import { Box, Button, Card, Fade, IconButton, Typography } from '@mui/material';
import { PropertySearchResTypeData } from '../../services/api';
import { useState } from 'react';
import { ShareModal } from '../../components/share-modal/share-modal.tsx';
import { FavoriteProperties } from '../../models/favorites.model.ts';
import { useNavigate } from 'react-router';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton.tsx';
import { ImageCarousel } from '../ImageCarousel/ImageCarousel.tsx';
import { useGetLandSearchGooglePhoto } from '../../api/search.ts';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { squareFeetToAcres, topLeftText } from '../../utils/search.ts';

import ReplyIcon from '@mui/icons-material/Reply';
import { theme } from '../../constants/theme/theme.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTimedState } from '../../hooks/use-timed-state.ts';

interface ResultsListItemProps {
  result: PropertySearchResTypeData;
  favorites?: FavoriteProperties[];
  showShareButton?: boolean;
  onMap?: boolean;
  isHome?: boolean;
  showmoveForward?: boolean;
}

export const ResultsListItem = (props: ResultsListItemProps) => {
  const {
    mlsListingPrice,
    address,
    name,
    bedrooms,
    bathrooms,
    basePriceRange,
    lotSquareFeet,
    mlsDaysOnMarket,
    mlsSold,
    lastSaleAmount,
    latitude,
    longitude,
    apn,
    id,
  } = props.result;

  const { favorites, showShareButton = true } = props;

  // TODO: The following needs to be moved upwards to the page file as per LRS.
  const [showingShareModal, setShowingShareModal] = useState(false);
  const navigate = useNavigate();

  const photo = useGetLandSearchGooglePhoto(props.result);

  // Everything below this line is fine to stay where it is.
  const imageURL = props.result.primaryListingImageUrl
    ? props.result.primaryListingImageUrl
    : `data:image/png;base64,${photo.data?.base64Image}`;

  const badgeText = topLeftText(mlsDaysOnMarket);

  const getPriceRangeText = (rangeArray: number[]) => {
    let text = 'Starting from';

    if (rangeArray[0]) text += ` $${rangeArray[0].toLocaleString()}`;

    if (rangeArray[1]) text += ` - $${rangeArray[1].toLocaleString()}`;

    return text;
  };
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxHeight = isMobile ? '300px' : '254px';

  const [showFavoriteMessage, setShowFavoriteMessage] = useTimedState(
    false,
    2500
  );

  const handleAddToFavorites = () => {
    setShowFavoriteMessage(true);
  };

  return (
    <Card className="results-list-item" elevation={0}>
      {badgeText && <Box className="badge">{badgeText}</Box>}
      <Box className="options-container">
        {showShareButton && (
          <IconButton
            aria-label="share"
            color="primary"
            className="share-button"
            onClick={() => {
              setShowingShareModal(true);
            }}
          >
            <div className="button-tooltip">Share</div>
            <ReplyIcon sx={{ color: '#49454F', transform: 'scaleX(-1)' }} />
          </IconButton>
        )}
        {favorites && (
          // TODO: The following needs to be moved upwards to the page file as per LRS.
          <FavoriteButton
            id={id || ''}
            favorites={favorites || []}
            addtoFavoritesFunc={handleAddToFavorites}
            result={
              {
                latitude: latitude,
                longitude: longitude,
                apn: apn,
                address: address,
                id: id,
              } as FavoriteProperties
            }
          />
        )}
      </Box>

      {props.result.photosList?.length == 1 ? (
        <LazyLoadImage
          alt="property"
          effect="blur"
          src={props.result.photosList[0]}
          width="334px"
          height={maxHeight}
          style={{ objectFit: 'cover' }}
          useIntersectionObserver={true}
        />
      ) : (
        <ImageCarousel
          propertySearchData={props.result}
          mlsImage={imageURL}
          showArrows={true}
          smallArrows={true}
          height="100%"
          onClick={() => void navigate(`/details?id=${id}`)}
        />
      )}
      {!props.isHome ? (
        <Box
          className="info"
          onClick={() => void navigate(`/details?id=${id}`)}
        >
          <Typography variant="h3">
            {address?.address}, {address?.city}
            <br />
            {address?.state} {address?.zip}
          </Typography>
          <Typography className="price">
            $
            {mlsSold
              ? lastSaleAmount?.toLocaleString() || '--'
              : mlsListingPrice?.toLocaleString() || '--'}
          </Typography>
          <Box className="lot-size">
            <Typography>
              {`${lotSquareFeet?.toLocaleString() || '--'} sq. ft. `}
              &#8226;
              {` ${squareFeetToAcres(lotSquareFeet || 0)?.toLocaleString() || '--'} acres`}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box className="info-home-model">
          <Typography className="name">{name}</Typography>
          <Box className="lot-size">
            <Typography>{`${bedrooms} Bed | ${bathrooms} Bath | ${lotSquareFeet} sqft.`}</Typography>
          </Box>
          <Typography className="text-light price-range">
            {getPriceRangeText(basePriceRange || [])}
          </Typography>
          <Box className="card-button">
            <Button variant="contained">Customize my home</Button>
          </Box>
        </Box>
      )}

      <ShareModal
        isOpen={showingShareModal}
        setIsOpen={setShowingShareModal}
        selectedPropertyId={`details?id=${id}`}
      />
      <Box className="favorite-alert">
        <Fade in={showFavoriteMessage} timeout={{ enter: 500, exit: 500 }}>
          <Button variant="contained" sx={{ cursor: 'default' }}>
            Favorite added to My Portal
          </Button>
        </Fade>
      </Box>
    </Card>
  );
};


